/**
 * useShopStatus Hook
 *
 * Fetches shop status for ShopLayout.
 * Used to validate shop membership, role, and subscription state.
 */

import { useQuery } from '@tanstack/react-query';
import { bootstrap } from '@/shared/api/bootstrap';
import type { ShopStatusResponse } from '@/shared/api/bootstrap';
import { logger } from '@/shared/utils/logger';
import { bootstrapKeys } from './useBootstrap';

export interface UseShopStatusOptions {
  shopSlug: string;
  enabled?: boolean;
  accessToken?: string | null;
}

export interface UseShopStatusReturn {
  data: ShopStatusResponse | null;
  isLoading: boolean;
  error: Error | null;
  refetch: () => Promise<unknown>;
}

/**
 * Hook to fetch shop status for ShopLayout
 */
export const useShopStatus = (options: UseShopStatusOptions): UseShopStatusReturn => {
  const { shopSlug, enabled = true, accessToken } = options;

  const {
    data,
    isLoading,
    error,
    refetch,
  } = useQuery<ShopStatusResponse>({
    queryKey: bootstrapKeys.shopStatus(shopSlug),
    queryFn: async () => {
      logger.info('Fetching shop status', { shopSlug });
      const result = await bootstrap.getShopStatus(shopSlug, accessToken);
      logger.info('Shop status received', {
        shopId: result.shop.id,
        shopName: result.shop.name,
        role: result.role,
        subscriptionStatus: result.subscriptionStatus,
        permissionCount: result.permissions.length,
      });
      return result;
    },
    enabled: enabled && !!shopSlug,
    staleTime: 60 * 1000, // 1 minute
    gcTime: 5 * 60 * 1000, // 5 minutes
    retry: 2,
    refetchOnWindowFocus: false,
  });

  return {
    data: data || null,
    isLoading,
    error: error as Error | null,
    refetch,
  };
};
